import { UUID } from 'crypto';
import { isDateBefore, removeTimezoneOffset } from 'odfjell-date-time-utils/dateTimeUtils';
import { getBunkeringReference, getLatestStatus } from 'src/components/bunkering/BunkeringUtils';
import { BunkerLossBM } from 'src/types/BusinessModel/Bunkering/BunkerLossBM';
import { BunkerReceivedBM } from 'src/types/BusinessModel/Bunkering/BunkerReceivedBM';
import { BunkeringBM } from 'src/types/BusinessModel/Bunkering/BunkeringBM';
import { BunkeringEventBM } from 'src/types/BusinessModel/Bunkering/BunkeringEventBM';
import { BunkeringStatusBM } from 'src/types/BusinessModel/Bunkering/BunkeringStatusBM';
import { FuelRobBM } from 'src/types/BusinessModel/Bunkering/FuelRobBM';
import { getBioFuelType, getBioFuelTypeId } from 'src/types/ViewModel/Bunkering/BioFuelType';
import { getBiofuelGrade, getBiofuelGradeId } from 'src/types/ViewModel/Bunkering/BiofuelGrade';
import { BunkerLoss } from 'src/types/ViewModel/Bunkering/BunkerLoss';
import { BunkerLossReason } from 'src/types/ViewModel/Bunkering/BunkerLossReason';
import { BunkerReceived } from 'src/types/ViewModel/Bunkering/BunkerReceived';
import { Bunkering } from 'src/types/ViewModel/Bunkering/Bunkering';
import { BunkeringEvent } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { BunkeringStatus } from 'src/types/ViewModel/Bunkering/BunkeringStatus';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import { FuelType, getFuelType, getFuelTypeId } from 'src/types/ViewModel/Bunkering/FuelType';

export const mapEventFuelType = (fuelType: string): FuelType | null => {
    switch (fuelType) {
        case 'HFO':
        case 'HFO (For vessels with EGCS/Scrubbers)':
            return FuelType.HFO;
        case 'LSF':
        case 'VLSFO':
            return FuelType.LSF;
        case 'MGO':
        case 'LGO':
            return FuelType.LGO;
        case 'BIO':
            return FuelType.BIO;
        default:
            return null;
    }
};

export const mapBunkeringEventBM = (bm: BunkeringEventBM, fuelType: string, index: number): BunkeringEvent => {
    const mappedFuelType = mapEventFuelType(fuelType);

    return {
        id: `${bm.eventID}-${fuelType}`,
        eventID: bm.eventID,
        imoNumber: bm.imoNumber,
        bunkeringReference: getBunkeringReference(FuelType[mappedFuelType], bm.portUnCode, bm.portName, new Date(bm.bunkeringDateTimeLocal), index),
        fuelType: mappedFuelType,
        bunkeringDateTimeLocal: removeTimezoneOffset(bm.bunkeringDateTimeLocal),
        portId: bm.portId,
        portCallId: bm.portCallId,
        portUnCode: bm.portUnCode,
        portName: bm.portName,
        voyageNumber: bm.voyageNumber,
        debunkering: bm.debunkering ?? false,
    };
};

export const mapBunkeringEventBMs = (bms: BunkeringEventBM[]): BunkeringEvent[] => {
    if (!bms) {
        return [];
    }
    bms.sort((a, b) => (isDateBefore(new Date(a.bunkeringDateTimeLocal), new Date(b.bunkeringDateTimeLocal)) ? -1 : 1));

    const bunkeringEvents: BunkeringEvent[] = [];

    // Iterate through the bms and add an index based on fuelType and year
    bms.forEach((bm) => {
        bm.fuelTypes
            .filter((fuelType) => mapEventFuelType(fuelType) !== null)
            .forEach((fuelType) => {
                const index =
                    bunkeringEvents.filter(
                        (prevEv) =>
                            prevEv.fuelType === mapEventFuelType(fuelType) &&
                            new Date(prevEv.bunkeringDateTimeLocal).getFullYear() === new Date(bm.bunkeringDateTimeLocal).getFullYear()
                    ).length + 1;
                bunkeringEvents.push(mapBunkeringEventBM(bm, fuelType, index));
            });
    });

    return bunkeringEvents;
};

export const mapBunkeringStatusBM = (bm: BunkeringStatusBM): BunkeringStatus => {
    return {
        bunkeringStatusId: bm.bunkeringStatusId,
        bunkeringStatusGuid: bm.bunkeringStatusGuid as UUID,
        bunkeringId: bm.bunkeringId,
        bunkeringGuid: bm.bunkeringGuid,
        status: bm.status,
        activeFromDate: new Date(bm.activeFromDate),
        updatedBy: bm.updatedBy,
        updatedAt: new Date(bm.updatedAt),
    };
};

export const mapBunkeringStatusBMs = (bms: BunkeringStatusBM[]): BunkeringStatus[] => (bms ? bms.map(mapBunkeringStatusBM) : []);

export const mapBunkeringStatus = (status: BunkeringStatus): BunkeringStatusBM => {
    return {
        bunkeringStatusId: status.bunkeringStatusId,
        bunkeringStatusGuid: status.bunkeringStatusGuid,
        bunkeringId: status.bunkeringId,
        bunkeringGuid: status.bunkeringGuid,
        status: status.status,
        activeFromDate: status.activeFromDate,
        updatedBy: status.updatedBy,
        updatedAt: new Date(status.updatedAt),
    };
};

export const mapBunkeringStatuses = (statuses: BunkeringStatus[]): BunkeringStatusBM[] => (statuses ? statuses.map(mapBunkeringStatus) : []);

export const mapBunkerReceivedBM = (bm: BunkerReceivedBM): BunkerReceived => {
    return {
        bunkerInTankId: bm.bunkerInTankId,
        bunkeringId: bm.bunkeringId,
        bunkeringGuid: bm.bunkeringGuid,
        tankId: bm.tankId,
        bunkerReceivedMt: bm.debunkeringEventId && bm.bunkerReceivedMt < 0 ? bm.bunkerReceivedMt * -1 : bm.bunkerReceivedMt,
        debunkeringEventId: bm.debunkeringEventId,
    };
};

export const mapBunkerReceivedBMs = (bms: BunkerReceivedBM[]): BunkerReceived[] => (bms ? bms.map(mapBunkerReceivedBM) : []);

export const mapBunkerReceived = (received: BunkerReceived): BunkerReceivedBM => {
    return {
        bunkerInTankId: received.bunkerInTankId,
        bunkeringId: received.bunkeringId,
        bunkeringGuid: received.bunkeringGuid,
        tankId: received.tankId,
        bunkerReceivedMt: received.bunkerReceivedMt,
        debunkeringEventId: received.debunkeringEventId,
    };
};

export const mapBunkerReceiveds = (receiveds: BunkerReceived[]): BunkerReceivedBM[] =>
    receiveds ? receiveds.filter((r) => r.bunkerReceivedMt && r.bunkerReceivedMt > 0).map(mapBunkerReceived) : [];

const calculateLiterWeightDifference = (bunkeringBM: BunkeringBM): number => {
    if (!bunkeringBM.bdnLiterWeight || !bunkeringBM.ftaLiterWeight) return 0;
    const totalBunkerReceived = bunkeringBM.bunkerReceivedPerTanks.reduce((acc, br) => acc + br.bunkerReceivedMt, 0);
    if (totalBunkerReceived === 0) return 0;
    const bdnTotalVolume = totalBunkerReceived / bunkeringBM.bdnLiterWeight;
    const ftaWeight = bdnTotalVolume * bunkeringBM.ftaLiterWeight;
    return Math.abs(totalBunkerReceived - ftaWeight);
};

export const mapBunkeringBM = (bm: BunkeringBM, bunkeringEvent?: BunkeringEvent): Bunkering => {
    const statuses = mapBunkeringStatusBMs(bm.bunkeringStatuses);
    return {
        bunkeringId: bm.bunkeringId,
        bunkeringGuid: bm.bunkeringGuid as UUID,
        imoNumber: bm.imoNumber,
        eventId: bm.eventId as UUID,
        bunkeringReference: bm.bunkeringReference,
        bdnReference: bm.bdnReference,
        bunkeringDateTime: bunkeringEvent ? new Date(bunkeringEvent.bunkeringDateTimeLocal) : new Date(bm.bunkeringDateTime),
        fuelType: bm.fuelTypeId as FuelType,
        bioFuelType: getBioFuelType(bm.bioFuelTypeId),
        sulphur: bm.sulphur,
        bdnLiterWeight: bm.bdnLiterWeight,
        ftaLiterWeight: bm.ftaLiterWeight,
        fuelViscosity: bm.fuelViscosity,
        bioBlend: bm.bioBlend,
        bioBlendFossilFuelType: getFuelType(bm.bioBlendFossilFuelTypeId),
        lowerHeatingValue: bm.lowerHeatingValue,
        euLowerCalorificValue: bm.euLowerCalorificValue,
        euGhgEmissions: bm.euGhgEmissions,
        imoGhgEmissions: bm.imoGhgEmissions,
        sustainabilityReference: bm.sustainabilityReference,
        lastUpdatedBy: bm.lastUpdatedBy,
        lastUpdatedAt: new Date(bm.lastUpdatedAt),
        approvedForUseAt: bm.approvedForUseAt ? new Date(bm.approvedForUseAt) : undefined,
        proofOfSustainability: bm.proofOfSustainability,
        waterContent: bm.waterContent,
        totalReceivedQuantity: bm.totalReceivedQuantity,
        biofuelMass: bm.biofuelMass,
        biofuelGrade: getBiofuelGrade(bm.biofuelGradeId),
        biofuelEnergyContent: bm.biofuelEnergyContent,
        debunkeringEventIds: bm.debunkeringEventIds.map((id) => id as UUID),
        bunkerReceivedPerTanks: mapBunkerReceivedBMs(bm.bunkerReceivedPerTanks.filter((br) => !br.debunkeringEventId)),
        debunkeringData: mapBunkerReceivedBMs(bm.bunkerReceivedPerTanks.filter((br) => br.debunkeringEventId)),
        currentStatus: getLatestStatus(statuses),
        bunkeringStatuses: statuses,
        bunkeringEvent: bunkeringEvent,
        literWeightDifference: calculateLiterWeightDifference(bm),
        deleted: bm.deleted ?? false,
    };
};

export const mapBunkeringBMs = (
    bms: BunkeringBM[],
    events: BunkeringEvent[],
    offlineBunkerings: any[],
    offlineBunkeringStatuses: any[]
): { bunkerings: Bunkering[]; deletedBunkerings: Bunkering[] } => {
    if (!bms) bms = [];
    addOfflineDataToBunkeringBMs(bms, offlineBunkerings, offlineBunkeringStatuses);

    const bunkerings = bms
        // Filter out bunkerings where no corresponding event exists (event may have been deleted in port log)
        .filter((bm) => events.some((event) => event.eventID === bm.eventId))
        .map((bm) =>
            mapBunkeringBM(
                bm,
                events.find((event) => event.eventID === bm.eventId)
            )
        );

    const deletedBunkerings = bms.filter((bm) => !events.some((event) => event.eventID === bm.eventId)).map((bm) => mapBunkeringBM(bm));

    return { bunkerings, deletedBunkerings };
};

const addOfflineDataToBunkeringBMs = (bms: BunkeringBM[], offlineBunkerings: any[], offlineBunkeringStatuses: any[]) => {
    offlineBunkerings.forEach((offlineBunkering) => {
        const upsertBunkeringRequest = offlineBunkering.upsertBunkeringRequest;
        if (upsertBunkeringRequest) {
            const bunkeringResponse = upsertBunkeringRequest.response;
            const indexOfBM = bms.findIndex((bm) => bm.bunkeringGuid === bunkeringResponse.bunkeringGuid);

            if (indexOfBM > -1) {
                bms[indexOfBM] = bunkeringResponse;
            } else {
                bms.push(bunkeringResponse);
            }
        }
    });

    offlineBunkeringStatuses
        .sort((a, b) => a.lastUpdated - b.lastUpdated)
        .forEach((offlineBunkeringStatus) => {
            const upsertBunkeringStatusRequest = offlineBunkeringStatus.upsertBunkeringStatusRequest;
            const deleteBunkeringStatusRequest = offlineBunkeringStatus.deleteBunkeringStatusRequest;

            if (upsertBunkeringStatusRequest) {
                const bunkeringStatusResponse = upsertBunkeringStatusRequest.response;
                const bm = bms.find((bm) => bm.bunkeringGuid === bunkeringStatusResponse.bunkeringGuid);

                if (bm) {
                    if (!bm.bunkeringStatuses) bm.bunkeringStatuses = [];

                    const indexOfBunkeringStatusBM = bm.bunkeringStatuses.findIndex(
                        (bunkeringStatusBM) => bunkeringStatusBM.bunkeringStatusGuid === bunkeringStatusResponse.bunkeringStatusGuid
                    );

                    if (indexOfBunkeringStatusBM > -1) {
                        bm.bunkeringStatuses[indexOfBunkeringStatusBM] = bunkeringStatusResponse;
                    } else {
                        bm.bunkeringStatuses.push(bunkeringStatusResponse);
                    }
                }
            }
            if (deleteBunkeringStatusRequest) {
                const bunkeringStatusResponse = deleteBunkeringStatusRequest.response;
                const bm = bms.find((bunkeringBM) =>
                    bunkeringBM.bunkeringStatuses.find(
                        (bunkeringStatusBM) => bunkeringStatusBM.bunkeringStatusGuid === bunkeringStatusResponse.bunkeringStatusGuid
                    )
                );

                if (bm) {
                    const indexOfBunkeringStatusBM = bm.bunkeringStatuses.findIndex(
                        (b) => b.bunkeringStatusGuid === bunkeringStatusResponse.bunkeringStatusGuid
                    );
                    if (indexOfBunkeringStatusBM !== -1) {
                        bm.bunkeringStatuses.splice(indexOfBunkeringStatusBM, 1);
                    }
                }
            }
        });
};

export const mapBunkering = (bunkering: Bunkering): BunkeringBM => {
    return {
        bunkeringId: bunkering.bunkeringId,
        bunkeringGuid: bunkering.bunkeringGuid,
        imoNumber: bunkering.imoNumber,
        eventId: bunkering.eventId,
        bunkeringReference: bunkering.bunkeringReference,
        bdnReference: bunkering.bdnReference,
        bunkeringDateTime: bunkering.bunkeringDateTime,
        fuelTypeId: bunkering.fuelType,
        bioFuelTypeId: bunkering.bioFuelType ? getBioFuelTypeId(bunkering.bioFuelType) : null,
        sulphur: bunkering.sulphur,
        bdnLiterWeight: bunkering.bdnLiterWeight,
        ftaLiterWeight: bunkering.ftaLiterWeight,
        fuelViscosity: bunkering.fuelViscosity,
        bioBlend: bunkering.bioBlend,
        bioBlendFossilFuelTypeId: bunkering.bioBlendFossilFuelType ? getFuelTypeId(bunkering.bioBlendFossilFuelType) : null,
        euLowerCalorificValue: bunkering.euLowerCalorificValue,
        lowerHeatingValue: bunkering.lowerHeatingValue,
        euGhgEmissions: bunkering.euGhgEmissions,
        imoGhgEmissions: bunkering.imoGhgEmissions,
        sustainabilityReference: bunkering.sustainabilityReference,
        lastUpdatedBy: bunkering.lastUpdatedBy,
        lastUpdatedAt: bunkering.lastUpdatedAt,
        approvedForUseAt: bunkering.approvedForUseAt,
        proofOfSustainability: bunkering.proofOfSustainability,
        waterContent: bunkering.waterContent,
        debunkeringEventIds: bunkering.debunkeringEventIds,
        totalReceivedQuantity: bunkering.totalReceivedQuantity,
        biofuelMass: bunkering.biofuelMass,
        biofuelGradeId: bunkering.biofuelGrade ? getBiofuelGradeId(bunkering.biofuelGrade) : null,
        biofuelEnergyContent: bunkering.biofuelEnergyContent,
        bunkerReceivedPerTanks: mapBunkerReceiveds(bunkering.bunkerReceivedPerTanks),
        bunkeringStatuses: mapBunkeringStatuses(bunkering.bunkeringStatuses),
        deleted: bunkering.deleted ?? false,
    };
};

export const mapFuelRob = (fuelRob: FuelROB): FuelRobBM => {
    return {
        fuelRobTankId: fuelRob.fuelRobId,
        bunkeringId: fuelRob.bunkeringId,
        bunkeringGuid: fuelRob.bunkeringGuid,
        noonReportId: fuelRob.noonReportId,
        tankId: fuelRob.tankId,
        rob: fuelRob.rob,
        soundingVerified: fuelRob.soundingVerified,
    };
};

export const mapFuelRobs = (fuelRobs: FuelROB[]): FuelRobBM[] => (fuelRobs ? fuelRobs.map(mapFuelRob) : []);

export const mapFuelRobBM = (bm: FuelRobBM): FuelROB => {
    return {
        fuelRobId: bm.fuelRobTankId,
        bunkeringId: bm.bunkeringId,
        bunkeringGuid: bm.bunkeringGuid as UUID,
        noonReportId: bm.noonReportId,
        tankId: bm.tankId,
        rob: bm.rob,
        soundingVerified: bm.soundingVerified,
    };
};

export const mapFuelRobBMs = (bms: FuelRobBM[], offlineFuelRobs: any[]): FuelROB[] => {
    if (!bms) bms = [];

    addOfflineDataToFuelRobBMs(bms, offlineFuelRobs);

    return bms.map(mapFuelRobBM);
};

const addOfflineDataToFuelRobBMs = (bms: FuelRobBM[], offlineFuelRobs: any[]) => {
    offlineFuelRobs.forEach((offlineFuelRob) => {
        const upsertFuelRobDataRequest = offlineFuelRob.upsertFuelRobDataRequest;

        if (upsertFuelRobDataRequest) {
            const fuelRobBody = upsertFuelRobDataRequest.body;

            fuelRobBody.forEach((offlineFuelRobTank) => {
                const indexOfBM = bms.findIndex(
                    (bm) => bm.bunkeringGuid === offlineFuelRobTank.bunkeringGuid && bm.fuelRobTankId === offlineFuelRobTank.fuelRobTankId
                );

                if (indexOfBM > -1) {
                    bms[indexOfBM] = offlineFuelRobTank;
                } else {
                    bms.push(offlineFuelRobTank);
                }
            });
        }
    });
};

export const mapBunkerLoss = (bunkerLoss: BunkerLoss): BunkerLossBM => {
    return {
        bunkerLossId: bunkerLoss.bunkerLossId,
        bunkerLossGuid: bunkerLoss.bunkerLossGuid,
        noonReportId: bunkerLoss.noonReportId,
        bunkeringId: bunkerLoss.bunkeringId,
        bunkeringGuid: bunkerLoss.bunkeringGuid,
        tankId: bunkerLoss.tankId,
        reasonId: bunkerLoss.reason as number,
        comment: bunkerLoss.comment,
        quantityMT: bunkerLoss.quantityMT,
    };
};

export const mapBunkerLossData = (bunkerLossData: BunkerLoss[]): BunkerLossBM[] => (bunkerLossData ? bunkerLossData.map(mapBunkerLoss) : []);

export const mapBunkerLossBM = (bm: BunkerLossBM): BunkerLoss => {
    return {
        bunkerLossId: bm.bunkerLossId,
        bunkerLossGuid: bm.bunkerLossGuid as UUID,
        noonReportId: bm.noonReportId,
        bunkeringId: bm.bunkeringId,
        bunkeringGuid: bm.bunkeringGuid as UUID,
        tankId: bm.tankId,
        reason: bm.reasonId as BunkerLossReason,
        comment: bm.comment,
        quantityMT: bm.quantityMT,
    };
};

export const mapBunkerLossBMs = (bms: BunkerLossBM[]): BunkerLoss[] => (bms ? bms.map(mapBunkerLossBM) : []);
